import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useRole } from "@/lib/roles";
import { Plus, Pencil, Trash2, Eye, X, MessageCircle, Mail, Save, Loader2 } from "lucide-react";

const KIND_KEYS = ["convite", "confirmacao", "lembrete", "cobranca_suave", "cobranca_firme", "recusa", "dispensa", "cancelamento", "custom"];

const CHANNEL_OPTS = [
  { value: "whatsapp", icon: MessageCircle },
  { value: "email", icon: Mail },
  { value: "both", icon: null },
];

const PLACEHOLDER_KEYS = [
  "nome", "nome_curto", "evento", "data", "horario", "local", "dress_code",
  "dress_code_line", "valor_hora", "briefing", "agencia", "cliente", "recibo_numero",
  "recibo_total", "recibo_data", "link",
];

// Variables that map to Meta positional params ({{1}},{{2}}…). Must match the
// backend list in server.py (_WA_META_VARS) so the derived order is identical.
const META_VARS = [
  "nome_curto", "nome", "evento", "data", "horario", "local",
  "dress_code_line", "dress_code", "valor_hora", "briefing",
  "recibo_numero", "recibo_total", "recibo_data", "link", "cliente", "agencia",
];
const META_VAR_RE = new RegExp("\\{(" + META_VARS.join("|") + ")\\}", "g");
function toMetaBody(body) {
  const order = [];
  const meta = (body || "").replace(META_VAR_RE, (_, name) => {
    if (!order.includes(name)) order.push(name);
    return "{{" + (order.indexOf(name) + 1) + "}}";
  });
  return { meta, order };
}

const KIND_CLS = {
  convite: "bg-zinc-100 text-zinc-800 border-zinc-200",
  confirmacao: "bg-emerald-50 text-emerald-800 border-emerald-200",
  lembrete: "bg-amber-100 text-amber-800 border-amber-200",
  cobranca_suave: "bg-orange-50 text-orange-800 border-orange-200",
  cobranca_firme: "bg-red-100 text-red-800 border-red-200",
  recusa: "bg-rose-50 text-rose-800 border-rose-200",
  dispensa: "bg-purple-50 text-purple-800 border-purple-200",
  cancelamento: "bg-orange-100 text-orange-800 border-orange-200",
  custom: "bg-zinc-50 text-zinc-700 border-zinc-200",
};

export default function Templates() {
  const { t } = useTranslation("templates");
  const { t: tc } = useTranslation("common");
  const { canEdit } = useRole();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyMeta = (text) => {
    if (navigator.clipboard) navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const refresh = () => {
    setLoading(true);
    api.get("/templates").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, []);

  const startNew = () => setEditing({ name: "", kind: "custom", channel: "whatsapp", subject: "", body: "", wa_template_name: "", wa_template_language: "pt_PT" });
  const startEdit = (t) => setEditing({ ...t });

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: editing.name, kind: editing.kind, channel: editing.channel,
        subject: editing.subject || null, body: editing.body,
        wa_template_name: editing.wa_template_name || null,
        wa_template_language: editing.wa_template_name ? (editing.wa_template_language || "pt_PT") : null,
      };
      if (editing.id) await api.put(`/templates/${editing.id}`, payload);
      else await api.post("/templates", payload);
      setEditing(null);
      setPreview("");
      refresh();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (t2) => {
    if (!window.confirm(t("form.deleteConfirm", { name: t2.name }))) return;
    try {
      await api.delete(`/templates/${t2.id}`);
      refresh();
    } catch (e) {
      window.alert(e.response?.data?.detail || "Error");
    }
  };

  const insertPlaceholder = (ph) => {
    setEditing((cur) => ({ ...cur, body: (cur.body || "") + ph }));
  };

  const previewBody = async () => {
    if (!editing?.body) return;
    setPreviewing(true);
    try {
      // Get first event/promoter for preview
      const [{ data: ev }, { data: pr }] = await Promise.all([
        api.get("/events"),
        api.get("/promoters"),
      ]);
      const eventId = ev[0]?.id;
      const promoterId = pr[0]?.id;
      const { data: invList } = await api.get("/invoices");
      const invoiceId = invList?.invoices?.[0]?.id;
      const { data } = await api.post(`/templates/render-preview`, {
        event_id: eventId,
        promoter_id: promoterId,
        invoice_id: invoiceId,
        body_override: editing.body,
      });
      setPreview(data.rendered);
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="templates-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight text-zinc-950">{t("title")}</h1>
          <p className="text-sm text-zinc-500 mt-1">{t("subtitle")}</p>
        </div>
        <button onClick={startNew} data-testid="new-template-btn" className={`inline-flex items-center gap-2 bg-zinc-950 hover:bg-zinc-800 text-white px-4 py-2 rounded-lg text-sm font-semibold ${canEdit ? "" : "hidden"}`}>
          <Plus className="w-4 h-4" /> {t("newTemplate")}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {loading ? <div className="text-sm text-zinc-500 col-span-full text-center py-10">{tc("states.loading")}</div> : items.map((tpl) => {
          const channelOpt = CHANNEL_OPTS.find((c) => c.value === tpl.channel);
          const ChannelIcon = channelOpt?.icon;
          return (
            <div key={tpl.id} data-testid={`template-${tpl.id}`} className="bg-white border border-zinc-200 rounded-xl p-5 flex flex-col">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-zinc-900">{tpl.name}</h3>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${KIND_CLS[tpl.kind] || KIND_CLS.custom}`}>{t(`kinds.${tpl.kind}`, tpl.kind)}</span>
                  {ChannelIcon && <ChannelIcon className="w-3.5 h-3.5 text-zinc-400" />}
                  {tpl.is_default && <span className="text-[10px] text-zinc-500 bg-zinc-100 px-1.5 py-0.5 rounded-full">{t("default")}</span>}
                </div>
                <div className="flex gap-1">
                  <button onClick={() => startEdit(tpl)} data-testid={`edit-${tpl.id}`} className="p-1.5 hover:bg-zinc-100 rounded text-zinc-600" title={tc("actions.edit")}><Pencil className="w-3.5 h-3.5" /></button>
                  {!tpl.is_default && (
                    <button onClick={() => handleDelete(tpl)} data-testid={`delete-${tpl.id}`} className="p-1.5 hover:bg-red-50 rounded text-red-600" title={tc("actions.delete")}><Trash2 className="w-3.5 h-3.5" /></button>
                  )}
                </div>
              </div>
              <div className="text-xs text-zinc-600 whitespace-pre-wrap font-mono bg-zinc-50 border border-zinc-100 rounded-lg p-3 line-clamp-6 flex-1">{tpl.body}</div>
            </div>
          );
        })}
        {!loading && items.length === 0 && (
          <div className="col-span-full text-center py-12 text-sm text-zinc-500 bg-white border border-zinc-200 rounded-xl">{t("empty")}</div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 overflow-y-auto">
          <form onSubmit={handleSave} className="bg-white rounded-2xl w-full max-w-4xl my-8 p-6 space-y-5" data-testid="template-modal">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-bold text-zinc-950">{editing.id ? t("form.editTitle") : t("form.newTitle")}</h2>
              <button type="button" onClick={() => { setEditing(null); setPreview(""); }} className="p-1 rounded hover:bg-zinc-100"><X className="w-4 h-4" /></button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2 space-y-1.5">
                <label className="text-xs font-medium text-zinc-700 uppercase tracking-wide">{t("form.name")}</label>
                <input data-testid="tpl-name" required value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="form-input" />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-zinc-700 uppercase tracking-wide">{t("form.kind")}</label>
                <select data-testid="tpl-kind" value={editing.kind} onChange={(e) => setEditing({ ...editing, kind: e.target.value })} className="form-input">
                  {KIND_KEYS.map((k) => <option key={k} value={k}>{t(`kinds.${k}`)}</option>)}
                </select>
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-zinc-700 uppercase tracking-wide">{t("form.channel")}</label>
              <div className="flex gap-2 flex-wrap" data-testid="tpl-channel">
                {CHANNEL_OPTS.map((c) => (
                  <button key={c.value} type="button" onClick={() => setEditing({ ...editing, channel: c.value })} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border ${editing.channel === c.value ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-700 border-zinc-200"}`}>
                    {c.icon && <c.icon className="w-3.5 h-3.5" />} {t(`channels.${c.value}`)}
                  </button>
                ))}
              </div>
            </div>

            {(editing.channel === "email" || editing.channel === "both") && (
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-zinc-700 uppercase tracking-wide">{t("form.subject")}</label>
                <input data-testid="tpl-subject" value={editing.subject || ""} onChange={(e) => setEditing({ ...editing, subject: e.target.value })} className="form-input" />
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_240px] gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-zinc-700 uppercase tracking-wide">{t("form.body")}</label>
                <textarea data-testid="tpl-body" required value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} rows={10} className="form-input font-mono text-sm" />
              </div>
              <div className="space-y-2 max-h-72 overflow-y-auto">
                <div className="text-xs font-semibold text-zinc-700 uppercase tracking-wide">{t("form.placeholders")}</div>
                {PLACEHOLDER_KEYS.map((key) => {
                  const ph = `{${key}}`;
                  return (
                    <button key={ph} type="button" onClick={() => insertPlaceholder(ph)} data-testid={`placeholder-${key}`} className="text-left w-full text-xs font-mono bg-zinc-50 hover:bg-zinc-100 border border-zinc-200 rounded px-2 py-1.5">
                      <div className="font-semibold text-zinc-900">{ph}</div>
                      <div className="text-[10px] text-zinc-500 font-sans">{t(`placeholderDescriptions.${key}`)}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {editing.channel !== "email" && (() => {
              const { meta, order } = toMetaBody(editing.body || "");
              return (
                <div className="rounded-xl border border-blue-200 bg-blue-50/40 p-4 space-y-3" data-testid="meta-template-section">
                  <div className="flex items-center gap-2">
                    <MessageCircle className="w-4 h-4 text-blue-700" />
                    <div className="text-sm font-semibold text-blue-900">Template Meta (WhatsApp Cloud API)</div>
                  </div>
                  <p className="text-xs text-blue-800/80">
                    {"A Meta exige texto com variáveis numeradas ({{1}}, {{2}}…). Cria o template no Gestor da Meta com o texto abaixo — a PromoCrew preenche as variáveis automaticamente ao enviar. A ordem tem de ser exatamente esta."}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="text-[11px] font-medium text-zinc-700 uppercase tracking-wide">Nome do template na Meta</label>
                      <input data-testid="tpl-wa-name" value={editing.wa_template_name || ""} onChange={(e) => setEditing({ ...editing, wa_template_name: e.target.value })} placeholder="ex.: convite_evento" className="form-input" />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[11px] font-medium text-zinc-700 uppercase tracking-wide">Idioma</label>
                      <input data-testid="tpl-wa-language" value={editing.wa_template_language || "pt_PT"} onChange={(e) => setEditing({ ...editing, wa_template_language: e.target.value })} placeholder="pt_PT" className="form-input" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <label className="text-[11px] font-medium text-zinc-700 uppercase tracking-wide">Texto para colar na Meta</label>
                      <button type="button" onClick={() => copyMeta(meta)} data-testid="copy-meta-body" className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline font-medium">{copied ? "Copiado!" : "Copiar"}</button>
                    </div>
                    <div className="text-xs text-zinc-800 whitespace-pre-wrap font-mono bg-white border border-blue-200 rounded-lg p-3 min-h-[3rem]" data-testid="meta-body-preview">
                      {meta || <span className="text-zinc-400 font-sans">Escreve o corpo com variáveis (ex.: {"{nome_curto}"}) para veres aqui a versão para a Meta.</span>}
                    </div>
                  </div>
                  {order.length > 0 && (
                    <div className="space-y-1.5" data-testid="meta-var-legend">
                      <div className="text-[11px] font-medium text-zinc-700 uppercase tracking-wide">Correspondência de variáveis</div>
                      <div className="flex flex-wrap gap-1.5">
                        {order.map((v, i) => (
                          <span key={v} className="inline-flex items-center gap-1 text-[11px] bg-white border border-blue-200 rounded-full px-2 py-0.5">
                            <span className="font-mono font-semibold text-blue-700">{`{{${i + 1}}}`}</span>
                            <span className="text-zinc-400">=</span>
                            <span className="font-mono text-zinc-700">{v}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="flex items-center gap-2">
              <button type="button" onClick={previewBody} disabled={previewing} data-testid="preview-btn" className="inline-flex items-center gap-2 px-3 py-2 border border-zinc-200 rounded-lg text-sm hover:bg-zinc-50 disabled:opacity-60">
                {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                {t("form.preview")}
              </button>
            </div>
            {preview && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-4" data-testid="preview-output">
                <div className="text-xs font-semibold text-emerald-800 uppercase tracking-wide mb-1.5">{t("form.previewTitle")}</div>
                <div className="text-sm text-zinc-800 whitespace-pre-wrap">{preview}</div>
              </div>
            )}

            <div className="flex gap-3 pt-2 border-t border-zinc-100">
              <button type="submit" disabled={saving} data-testid="save-template" className="inline-flex items-center gap-2 bg-zinc-950 hover:bg-zinc-800 text-white px-5 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-60">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                <Save className="w-4 h-4" />
                {editing.id ? t("form.save") : t("form.create")}
              </button>
              <button type="button" onClick={() => { setEditing(null); setPreview(""); }} className="px-5 py-2.5 rounded-lg border border-zinc-200 text-sm hover:bg-zinc-50">{tc("actions.cancel")}</button>
            </div>

            <style>{`.form-input{width:100%;padding:0.625rem 0.75rem;border-radius:0.5rem;border:1px solid #e4e4e7;font-size:0.875rem;background:#fff} .form-input:focus{outline:none;border-color:#09090b;box-shadow:0 0 0 2px #09090b}`}</style>
          </form>
        </div>
      )}
    </div>
  );
}
